import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../assets/images/Logo.png';

interface HeaderProps {
  isAdminPage?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isAdminPage = false }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(isAdminPage ? true : false);
  const [activeSection, setActiveSection] = useState('hero');
  
  // Detect scroll to change header appearance
  useEffect(() => {
    if (isAdminPage) {
      setScrolled(true);
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);
      
      // Update active section based on scroll position
      const sections = ['hero', 'about', 'products', 'contact'];
      
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop - 200) {
          setActiveSection(section);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAdminPage]);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Prevent body scrolling when menu is open
    document.body.style.overflow = isMenuOpen ? 'auto' : 'hidden';
  };

  const handleNavigation = (id: string) => {
    if (isAdminPage) {
      // Navigate to homepage first, then scroll to section
      navigate('/');
      // Need to wait a bit for the navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Direct scrolling on homepage
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
        // Re-enable scrolling
        document.body.style.overflow = 'auto';
        setActiveSection(id);
      }
    }
  };

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled || isAdminPage
          ? isAdminPage 
            ? 'bg-white py-2' 
            : 'bg-white shadow-lg py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center z-10">
          <Link 
            to="/"
            className="group relative flex items-center"
          >
            <div className="mr-2">
              <img src={Logo} alt="Mad Anglers Logo" className="h-10 w-10" />
            </div>
            <div className="text-2xl font-heading font-bold">
              <span className={`text-primary transition-colors`}>MAD</span>
              <span className={`${scrolled || isAdminPage ? 'text-accent' : 'text-white'} transition-colors`}>ANGLERS</span>
            </div>
            
            {/* Underline animation */}
            <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></div>
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {['about', 'products', 'contact'].map((section) => (
            <button
              key={section}
              onClick={() => handleNavigation(section)}
              className={`relative px-4 py-2 mx-1 font-medium rounded-full transition-all duration-300
                ${!isAdminPage && activeSection === section 
                  ? scrolled 
                    ? 'text-white bg-primary shadow-md' 
                    : 'text-white bg-white/20 backdrop-blur-sm' 
                  : scrolled || isAdminPage
                    ? 'text-text hover:text-primary' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                } capitalize`}
            >
              {section}
            </button>
          ))}
          
          {/* Admin Link */}
          <Link
            to="/admin"
            className={`relative px-4 py-2 mx-1 font-medium rounded-full transition-all duration-300
              ${isAdminPage 
                ? 'text-white bg-primary'
                : scrolled 
                  ? 'text-text hover:text-primary' 
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
          >
            Admin
          </Link>
          
          {/* CTA Button */}
          <a 
            href="tel:+27635071011" 
            className="ml-4 px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-full transition-all duration-300 shadow-lg hover:-translate-y-1 font-medium flex items-center btn-shine"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            Call Now
          </a>
        </nav>
        
        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden relative z-50 focus:outline-none w-10 h-10 rounded-full flex items-center justify-center" 
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <div className={`relative transition-all duration-300 ${isMenuOpen ? 'rotate-45' : ''}`}>
            <span className={`block absolute w-6 h-0.5 ${isMenuOpen ? 'bg-primary' : scrolled || isAdminPage ? 'bg-primary' : 'bg-white'} transition-all duration-300 transform ${isMenuOpen ? 'rotate-0' : '-translate-y-1.5'}`}></span>
            <span className={`block absolute w-6 h-0.5 ${isMenuOpen ? 'bg-primary' : scrolled || isAdminPage ? 'bg-primary' : 'bg-white'} transition-all duration-200 opacity-100 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block absolute w-6 h-0.5 ${isMenuOpen ? 'bg-primary' : scrolled || isAdminPage ? 'bg-primary' : 'bg-white'} transition-all duration-300 transform ${isMenuOpen ? '-rotate-90' : 'translate-y-1.5'}`}></span>
          </div>
        </button>
      </div>
      
      {/* Mobile Navigation - Full screen overlay */}
      <div 
        className={`fixed inset-0 bg-white transition-all duration-500 lg:hidden flex flex-col items-center justify-center z-40 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: '0', bottom: '0', left: '0', right: '0', position: 'fixed' }}
      >
        <nav className="flex flex-col items-center justify-center space-y-6 py-8 w-full h-full px-4">
          <div className="mb-8">
            <img src={Logo} alt="Mad Anglers Logo" className="h-20 w-20 mx-auto mb-4" />
            <div className="text-3xl font-heading font-bold text-center">
              <span className="text-primary">MAD</span>
              <span className="text-accent">ANGLERS</span>
            </div>
          </div>
          
          {['about', 'products', 'contact'].map((section, index) => (
            <button
              key={section}
              onClick={() => handleNavigation(section)}
              className="relative text-2xl font-heading font-semibold text-text hover:text-primary transition-all duration-300 capitalize px-8 py-2"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className={`relative z-10 ${!isAdminPage && activeSection === section ? 'text-primary' : ''}`}>{section}</span>
              {!isAdminPage && activeSection === section && (
                <span className="absolute inset-0 bg-primary/10 rounded-full"></span>
              )}
            </button>
          ))}
          
          {/* Admin Link Mobile */}
          <Link
            to="/admin"
            className={`relative text-2xl font-heading font-semibold ${isAdminPage ? 'text-primary' : 'text-text'} hover:text-primary transition-all duration-300 px-8 py-2`}
            onClick={() => {
              setIsMenuOpen(false);
              document.body.style.overflow = 'auto';
            }}
          >
            <span className="relative z-10">Admin</span>
            {isAdminPage && (
              <span className="absolute inset-0 bg-primary/10 rounded-full"></span>
            )}
          </Link>
          
          {/* Mobile CTA Button */}
          <a 
            href="tel:+27635071011" 
            className="mt-8 px-8 py-3 bg-primary hover:bg-primary/90 text-white rounded-full transition-all duration-300 shadow-lg hover:-translate-y-1 font-medium flex items-center btn-shine"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            Call Now
          </a>
          
          {/* Social Media Icons */}
          <div className="flex space-x-4 mt-8">
            {[
              { href: 'https://x.com/', icon: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z", name: "Twitter" },
              { href: 'https://www.facebook.com/', icon: "M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z", name: "Facebook" },
              { href: 'https://www.instagram.com/', icon: "M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z", name: "Instagram" }
            ].map((social, index) => (
              <a 
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 hover:text-primary hover:bg-gray-100 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d={social.icon} />
                </svg>
              </a>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header; 